import * as React from 'react';
import { when } from 'mobx';
import { observer } from 'mobx-react';
import { FocusZone, FocusZoneDirection, List } from 'office-ui-fabric-react';
import { Link } from 'react-router-dom';

import { PersonModel } from '@app/Models';

import PersonStore, { PersonModelWithSearchTerms } from '@app/Stores/PersonStore';
import { stringSearchService } from '@app/Services/StringSearchService';
import { Icon } from './Icon';
import { personStatusStore } from '@app/Stores/PersonStatusStore';

type PersonListProps = {
    personStore: PersonStore;
    selectedPersonId: string | null;
};

@observer
export class PersonList extends React.Component<PersonListProps, {}> {
    private _listRef: React.RefObject<List> = React.createRef();

    async componentDidMount(){
        const store = this.props.personStore;
        personStatusStore.subscribeOnChage(this._onUpdatePersonStatus);
        
        await when(() => store.dataLoading.isCompleted);

        const personList = store.filteredList;
        const selectedIndex = personList.findIndex(p => p.id === this.props.selectedPersonId);
        if (selectedIndex !== -1){
            this._listRef.current?.scrollToIndex(selectedIndex);
        }
    }

    componentWillUnmount(){
        personStatusStore.unSubscribeOnChage(this._onUpdatePersonStatus);
    }

    render() {
        const personList = this.props.personStore.filteredList;

        return (
            <FocusZone direction={FocusZoneDirection.vertical}>
                {personList ? <List<PersonModel> ref={this._listRef} items={personList} onRenderCell={this._onRenderCell} /> : null}
            </FocusZone>
        );
    }

    private _onRenderCell = (person?: PersonModelWithSearchTerms): React.ReactNode => {
        if (!person) return null;
        const { isSearchedByCode, isSearchedByFirstName, isSearchedByLastName, isSearchedById, searchTerms , textComparer } = this.props.personStore;

        const toUrl = '/person/' + person.code;

        const idMatches = isSearchedById && stringSearchService.markMatches(person.id, searchTerms, textComparer).filter(m => m.isMatch).length;

        let className = 'person-list-item';
        if (person.id === this.props.selectedPersonId){
            className += ' active';
        }
        if (person.isDeleted){
            className += ' deleted';
        }
        if (!person.hasActualContract){
            className += ' inactive';
        }

        const publishingPerson = personStatusStore.pendingPersons.get(person.id);
        const isPublishing = publishingPerson?.isPending ?? person.isPublishing;
        const publishingErrorMessage = publishingPerson?.errorMessage ?? person.publishingErrorMessage;

        return (
            <div className={className} data-is-focusable={true}>
                <Link to={toUrl} className="person-list-item-link">
                    <div className="person-list-item-name">{this._renderText(person.code, isSearchedByCode, person.terms)}, {this._renderText(person.firstName, isSearchedByFirstName, person.terms)} {this._renderText(person.lastName, isSearchedByLastName, person.terms)}</div>
                    <div className="person-list-item-department">Department: {person.department}</div>
                    {idMatches ? <div className="person-list-item-id">Id: {this._renderText(person.id, isSearchedByCode, person.terms)}</div> : null}
                    {person.isDeleted || !person.hasActualContract ? <div className="person-list-item-tags">
                        {person.isDeleted && <span className="badge badge-danger">Deleted</span>}
                        {!person.hasActualContract && <span className="badge badge-warning">Inactive</span>}
                    </div> : null}
                    {isPublishing && <div className={'person-list-item-pending' + (publishingErrorMessage ? ' error' : '')} title={publishingErrorMessage ? `Publishing failed: ${publishingErrorMessage}` : 'Publishing is scheduled'}>
                        <Icon name="clock" />
                    </div>}
                </Link>
            </div>
        );
    };

    private _renderText(text: string | undefined, shouldSearch: boolean, searchTerms?: string[]) {
        if (!shouldSearch || !searchTerms){
            return text;
        }

        const { textComparer } = this.props.personStore;

        const parts = stringSearchService.markMatches(text, searchTerms, textComparer);
        return (<>
            {parts.map((p, index) => p.isMatch ? <span className="text-match" key={'t' + index}>{p.text}</span> : p.text)}
        </>);
    }

    private _onUpdatePersonStatus = () => {
        this._listRef.current?.forceUpdate();
    };
}